import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/themeClasses.js";
import AppButton from "./AppButton.jsx";
import { Z_INDEX } from "./zIndex.js";
import { listSortTriggerClassName, filterDrawerTriggerClassName } from "../../design/tokens.js";
import { useFilterDrawerMode } from "./filterDrawerContext.js";
import { computeFixedPopoverPosition } from "../../utils/fixedPopoverPosition.js";

/**
 * Option: { key: string, label: string, section?: string, title?: string, fixedDir?: 'asc'|'desc' }
 *
 * Props:
 *  - value       : { key, dir }
 *  - onChange    : (next:{key,dir}) => void
 *  - options     : Array<Option>
 *  - className   : string
 *  - menuWidth   : number
 *  - allowDirToggle: (key) => boolean (overrides fixedDir if you want)
 *  - buttonLabel : (value) => string
 *  - defaultDirFor: (key) => 'asc'|'desc'
 */
export default function SortButton({
  value,
  onChange,
  options,
  className = "",
  menuWidth = 260,
  allowDirToggle,
  buttonLabel,
  defaultDirFor,
}) {
  const inFilterDrawer = useFilterDrawerMode();
  const id = useId(); // unique id for menu
  const menuId = `sort-menu-${id}`;
  const btnRef = useRef(null);
  const menuWrapRef = useRef(null);

  const dirFor = defaultDirFor || defaultDirForBuiltin;
  const canToggleDir =
    allowDirToggle ||
    ((key, opt) => (!opt?.fixedDir && key !== "neueste")); // fallback policy

  // ── Open/position state ───────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: menuWidth });
  const lastFocusRef = useRef(null);

  const groupSections = useMemo(() => groupBySection(options || []), [options]);

  // compute label for button
  const computeBtnLabel =
    buttonLabel ||
    ((v) => {
      const opt = (options || []).find((o) => o.key === v.key);
      const base = opt ? opt.label : "Sortieren";
      const fixed = opt?.fixedDir;
      const effDir = fixed || v.dir;
      const showDir = v.key !== "neueste";
      const dirStr = showDir ? ` · ${effDir === "asc" ? "A–Z" : "Z–A"}` : "";
      return `${base}${dirStr}`;
    });

  const openMenu = () => {
    if (!btnRef.current) return;
    lastFocusRef.current = document.activeElement;
    const r = btnRef.current.getBoundingClientRect();
    setPos(computeFixedPopoverPosition(r, { width: menuWidth, heightEstimate: 360 }));
    setOpen(true);
  };

  const toggleOpen = () => {
    if (inFilterDrawer) {
      setOpen(v => !v);
      return;
    }
    openMenu();
  };

  // close and restore focus
  const closeMenu = () => {
    setOpen(false);
    const el = lastFocusRef.current;
    if (el && typeof el.focus === "function") {
      setTimeout(() => el.focus(), 0);
    }
  };

  // outside/escape/resize/scroll handling (Portal; im Drawer nur Escape)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
      if (!inFilterDrawer && e.key === "Tab") closeMenu();
    };
    const onOutside = (e) => {
      if (inFilterDrawer) return;
      if (!menuWrapRef.current) return;
      if (!menuWrapRef.current.contains(e.target) && !btnRef.current?.contains(e.target)) {
        closeMenu();
      }
    };
    const onRecalc = () => {
      if (!inFilterDrawer) closeMenu();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onOutside, true);
    window.addEventListener("resize", onRecalc, { passive: true });
    window.addEventListener("scroll", onRecalc, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onOutside, true);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc);
    };
  }, [open, inFilterDrawer]);

  // Keyboard navigation inside the menu
  const itemRefs = useRef([]);
  itemRefs.current = [];
  const registerItemRef = (el) => {
    if (el) itemRefs.current.push(el);
  };
  useEffect(() => {
    if (!open) return;
    // Fokus gemäss flacher Reihenfolge (über alle Sections)
    const flat = (groupSections || []).flatMap((s) => s.items || []);
    const idxActive = flat.findIndex((o) => o.key === value?.key);
    const focusEl = itemRefs.current[idxActive >= 0 ? idxActive : 0];
    focusEl?.focus?.();
  }, [open, groupSections, value?.key]);

  const onItemKeyDown = (e, idx) => {
    if (!open) return;
    const list = itemRefs.current;
    if (!list.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      list[Math.min(idx + 1, list.length - 1)]?.focus?.();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      list[Math.max(idx - 1, 0)]?.focus?.();
    } else if (e.key === "Home") {
      e.preventDefault();
      list[0]?.focus?.();
    } else if (e.key === "End") {
      e.preventDefault();
      list[list.length - 1]?.focus?.();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      list[idx]?.click?.();
    }
  };

  const handlePick = (opt) => {
    const baseDir = opt.fixedDir || dirFor(opt.key);
    const keepOrDefault = value?.key === opt.key ? (opt.fixedDir || value.dir) : baseDir;
    onChange({ key: opt.key, dir: keepOrDefault });
    closeMenu();
  };

  const toggleDir = () => {
    const opt = (options || []).find((o) => o.key === value?.key);
    if (!opt) return;
    if (!canToggleDir(value?.key, opt)) return;
    const next = value.dir === "asc" ? "desc" : "asc";
    onChange({ ...value, dir: next });
    closeMenu();
  };

  const menuCls = "w-64 rounded-xl popover-panel p-1";
  const dividerCls = "my-1 h-px bg-border-muted";
  const sectionLabelCls = "px-3 py-1 text-[11px] uppercase tracking-wide text-muted";
  const itemActiveCls = "menu-item-active";
  const itemInactiveCls = "menu-item-idle";
  const itemFixedDirCls = "ml-2 text-[11px] text-muted";
  const dirBtnCls = "w-full text-left px-3 py-2 rounded-lg text-sm min-h-[44px] menu-item-idle";

  const menuBody = (
    <>
      {(() => {
        let gIdx = -1;
        return groupSections.map((sec, sIdx) => (
          <div key={`sec-${sIdx}`}>
            {sIdx > 0 && <div className={dividerCls} />}
            {sec.label ? <div className={sectionLabelCls}>{sec.label}</div> : null}
            {(sec.items || []).map((opt) => {
              gIdx += 1;
              const i = gIdx;
              const active = value?.key === opt.key;
              return (
                <AppButton
                  key={opt.key}
                  role="menuitemradio"
                  aria-checked={active}
                  onKeyDown={(e) => onItemKeyDown(e, i)}
                  ref={registerItemRef}
                  onClick={() => handlePick(opt)}
                  title={opt.title || opt.label}
                  variant="ghost"
                  type="button"
                  className={cn(
                    "w-full !justify-start text-left rounded-lg text-sm outline-none min-h-[44px] h-auto font-normal",
                    active ? itemActiveCls : itemInactiveCls,
                  )}
                >
                  {opt.label}
                  {active &&
                  (typeof canToggleDir === "function" ? canToggleDir(opt.key, opt) : opt.key !== "neueste")
                    ? " •"
                    : ""}
                  {opt.fixedDir ? (
                    <span className={itemFixedDirCls}>{opt.fixedDir === "asc" ? "A–Z" : "Z–A"}</span>
                  ) : null}
                </AppButton>
              );
            })}
          </div>
        ));
      })()}

      {(() => {
        const opt = (options || []).find((o) => o.key === value?.key);
        return opt && canToggleDir(value?.key, opt);
      })() ? (
        <>
          <div className={dividerCls} />
          <AppButton
            onClick={toggleDir}
            variant="ghost"
            role="menuitem"
            title="Richtung ändern"
            type="button"
            className={cn("!justify-start rounded-lg text-sm", dirBtnCls)}
          >
            Richtung: {value?.dir === "asc" ? "A–Z" : "Z–A"}
          </AppButton>
        </>
      ) : null}
    </>
  );

  const triggerBtn = (
    <AppButton
      ref={btnRef}
      onClick={toggleOpen}
      type="button"
      variant="secondary"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={menuId}
      className={cn(listSortTriggerClassName, "text-sm", inFilterDrawer && filterDrawerTriggerClassName, className)}
      title="Sortieren"
    >
      {computeBtnLabel(value || { key: "", dir: "asc" })}
    </AppButton>
  );

  if (inFilterDrawer) {
    return (
      <div className="w-full min-w-0">
        {triggerBtn}
        {open ? (
          <div
            ref={menuWrapRef}
            id={menuId}
            role="menu"
            aria-label="Sortieren"
            className={cn(menuCls, "mt-1.5 w-full max-h-[min(40dvh,320px)] overflow-auto border border-default")}
          >
            {menuBody}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {triggerBtn}

      {open
        ? createPortal(
          <div
            ref={menuWrapRef}
            id={menuId}
            role="menu"
            aria-label="Sortieren"
            style={{
              position: "fixed",
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: `${pos.width}px`,
              zIndex: Z_INDEX.selectPortal,
            }}
            className={menuCls}
          >
            {menuBody}
          </div>,
          document.body,
        )
        : null}
    </>
  );
}

/* ───────────────── helpers ───────────────── */

function groupBySection(opts) {
  const map = new Map();
  for (const o of opts) {
    const sec = o.section || "";
    if (!map.has(sec)) map.set(sec, []);
    map.get(sec).push(o);
  }
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Built-in default dir policy tuned for your keys:
 * - 'neueste'          → desc (immer)
 * - 'ueberfällig'      → desc (mehr Tage überfällig zuerst)
 * - 'mitkunde'         → desc (true zuerst)
 * - 'progress'         → asc  (0% → 100%)
 * - 'termin/start/end' → asc
 * - 'woche'            → asc (Heute → +7 Tage → später → kein Termin)
 * - else               → asc
 */
function defaultDirForBuiltin(key) {
  if (!key) return "asc";
  const s = String(key).toLowerCase();
  if (s === "neueste")     return "desc"; // immer neueste zuerst
  if (s === "ueberfällig") return "desc"; // mehr Tage überfällig zuerst
  if (s === "mitkunde")    return "desc"; // true zuerst
  if (s === "progress")    return "asc";  // 0% → 100%
  if (s === "woche")       return "asc";  // heute → +7 → später → kein Termin
  if (s === "ohnetermin")  return "asc";  // mit Termin → ohne Termin
  if (s === "termin" || s === "start" || s === "end") return "asc";
  return "asc";
}
